/**
 * Formatting-whitespace elision, applied once when a template compiles.
 *
 * An `html` template authored across several lines carries its indentation
 * into the DOM: `html\`\n  <tr>…</tr>\n\`` materializes THREE top-level nodes,
 * two of which are the author's newlines. Every keyed-repeat insertion and
 * removal is per node, so a 1,000-row list charges the document three DOM
 * calls per row and retains three nodes per row, for indentation nobody asked
 * to render.
 *
 * The rule is the one Vue's `whitespace: 'condense'`, Svelte and Angular all
 * settle on, and it is deliberately narrow: a whitespace-only text run is
 * dropped **only** when it contains a newline and both of its neighbours are
 * element tags (or the template boundary). A single space between two inline
 * elements — `<span>a</span> <span>b</span>` — is load-bearing in inline flow
 * and is never touched, because it has no newline. A newline-containing run is
 * authoring indentation, which no layout depends on.
 *
 * Three things are structurally exempt:
 *
 * - Whitespace beside a comment. Node parts compile to `<!--h:N-->` markers,
 *   so "neighbour is an element tag" is what keeps interpolation-adjacent text
 *   intact — `\n  ${a}\n  ${b}\n` keeps every run, and the marker/part index
 *   mapping is untouched because nothing between markers moves.
 * - `<pre>` (and `<listing>`) content, where whitespace is significant.
 * - Raw-text element content — `<script>`, `<style>`, `<textarea>`, `<title>`
 *   — which the HTML parser never tokenizes as markup and whose newlines can
 *   be syntax.
 *
 * This runs on the compiled static HTML, which is the single source both the
 * DOM path (`ensureDomTemplate`) and the string path (`renderToString`) build
 * from, so the two agree and hydration still aligns. It runs once per compile,
 * never per clone.
 */
/** Elements whose content the HTML parser reads as raw text, never as markup. */
const RAW_TEXT_ELEMENTS = new Set(['script', 'style', 'textarea', 'title']);
/** Elements that render their text content with whitespace preserved. */
const PRESERVE_WHITESPACE_ELEMENTS = new Set(['pre', 'listing']);
/**
 * A tag that asks CSS to preserve its whitespace, which `<pre>` alone cannot
 * detect. The framework ships `.whitespace-pre`, `-pre-line`, `-pre-wrap` and
 * `-break-spaces` as utility classes (plus every responsive prefix), so any
 * consumer element can render its newlines — and under `white-space: pre` a
 * newline between two child elements is a LINE BREAK, not indentation. Matched
 * on the authored tag text because that is all this pass can see; a rule that
 * reaches an element through a selector in a stylesheet is out of reach here
 * and is documented as the residual limit.
 */
const CLASS_PRESERVE_RE = /(class|className)\s*=\s*["'][^"']*\bwhitespace-(pre|pre-line|pre-wrap|break-spaces)\b/i;
const STYLE_PRESERVE_RE = /style\s*=\s*["'][^"']*white-space\s*:\s*(pre|pre-line|pre-wrap|break-spaces)/i;
/** True when the tag text asks CSS to preserve this element's whitespace. */
function preservesByCss(tagText) {
    return CLASS_PRESERVE_RE.test(tagText) || STYLE_PRESERVE_RE.test(tagText);
}
/** HTML whitespace, and nothing else, for the whole run. */
const WHITESPACE_ONLY_RE = /^[ \t\n\r\f]+$/;
/** A run with no newline is inline spacing, not indentation. */
const NEWLINE_RE = /[\n\r]/;
/** Everything, or nothing, is whitespace. */
const ALL_WHITESPACE_RE = /^[ \t\n\r\f]*$/;
/** A `<` that opens a real tag: a name, or a closing slash. */
const TAG_NAME_RE = /^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/;
/** Index just past the `>` closing the tag starting at `start`, quotes respected. */
function endOfTag(html, start) {
    let quote = null;
    for (let index = start + 1; index < html.length; index += 1) {
        const ch = html[index];
        if (quote) {
            if (ch === quote)
                quote = null;
            continue;
        }
        if (ch === '"' || ch === "'") {
            quote = ch;
            continue;
        }
        if (ch === '>')
            return index + 1;
    }
    return html.length;
}
/** Index just past `terminator`, or the end of the string when it never closes. */
function endOfRun(html, terminator, from) {
    const found = html.indexOf(terminator, from);
    return found === -1 ? html.length : found + terminator.length;
}
function tagInfo(html, start, end) {
    const match = TAG_NAME_RE.exec(html.slice(start, end));
    if (!match)
        return null;
    return {
        name: (match[2] ?? '').toLowerCase(),
        closing: match[1] === '/',
        selfClosing: html.slice(start, end).replace(/>$/, '').trimEnd().endsWith('/'),
    };
}
/**
 * Splits static HTML into tags, comments/declarations and text runs.
 *
 * A `<` not followed by a name, `/`, `!` or `?` is ordinary character data —
 * `5 < ${x}` reaches the compiler intact — so it extends the current text run
 * rather than opening a segment.
 */
function segmentHtml(html) {
    const lower = html.toLowerCase();
    const segments = [];
    let index = 0;
    let textStart = -1;
    let preserveDepth = 0;
    // Names of the currently open preserve scopes, so a scope opened by a class
    // closes on its own end tag rather than on the next `</pre>` to come along.
    const openPreserve = [];
    const flushText = (end) => {
        if (textStart >= 0 && end > textStart) {
            segments.push({ kind: 'text', start: textStart, end, preserved: preserveDepth > 0 });
        }
        textStart = -1;
    };
    while (index < html.length) {
        if (html[index] !== '<') {
            if (textStart < 0)
                textStart = index;
            index += 1;
            continue;
        }
        const next = html.at(index + 1) ?? '';
        if (next === '!' || next === '?') {
            flushText(index);
            const end = html.startsWith('<!--', index)
                ? endOfRun(html, '-->', index + 4)
                : endOfRun(html, '>', index + 2);
            segments.push({ kind: 'other', start: index, end, preserved: false });
            index = end;
            continue;
        }
        const tagEnd = endOfTag(html, index);
        const tag = tagInfo(html, index, tagEnd);
        // A bare `<` in text content, which the HTML parser also keeps as data, so
        // it extends the current run rather than opening a segment.
        if (tag === null) {
            if (textStart < 0)
                textStart = index;
            index += 1;
            continue;
        }
        const openTag = tag;
        flushText(index);
        segments.push({ kind: 'tag', start: index, end: tagEnd, preserved: false });
        const tagStart = index;
        index = tagEnd;
        if (openTag.closing) {
            if (openPreserve.length > 0 && openPreserve[openPreserve.length - 1] === openTag.name) {
                openPreserve.pop();
                preserveDepth -= 1;
            }
            continue;
        }
        if (openTag.selfClosing)
            continue;
        if (RAW_TEXT_ELEMENTS.has(openTag.name)) {
            // Raw text is consumed whole: nothing inside it is markup, and its
            // newlines can be syntax (a `//` comment, a template literal).
            const closeAt = lower.indexOf(`</${openTag.name}`, index);
            const contentEnd = closeAt === -1 ? html.length : closeAt;
            if (contentEnd > index) {
                segments.push({ kind: 'text', start: index, end: contentEnd, preserved: true });
            }
            index = contentEnd;
            continue;
        }
        if (PRESERVE_WHITESPACE_ELEMENTS.has(openTag.name) ||
            preservesByCss(html.slice(tagStart, tagEnd))) {
            preserveDepth += 1;
            openPreserve.push(openTag.name);
        }
    }
    flushText(html.length);
    return segments;
}
/**
 * True when this run is indentation between elements rather than content.
 *
 * Takes its neighbours rather than an index into the list: the decision is
 * entirely local, and passing `before`/`after` says so while removing the
 * question of whether `position - 1` and `position + 1` are in range.
 */
function isFormattingRun(html, before, segment, after) {
    if (segment.kind !== 'text' || segment.preserved)
        return false;
    const text = html.slice(segment.start, segment.end);
    if (!WHITESPACE_ONLY_RE.test(text) || !NEWLINE_RE.test(text))
        return false;
    // Absent means the template boundary — the start or end of the child list.
    // `other` means a comment, which is how every node part's marker appears.
    return (!before || before.kind === 'tag') && (!after || after.kind === 'tag');
}
/**
 * Removes authoring indentation from compiled static HTML.
 *
 * A template that is nothing but whitespace is returned untouched: it has no
 * elements to sit between, and emptying it would take away the only nodes its
 * parts could resolve against.
 */
export function elideFormattingWhitespace(html) {
    if (ALL_WHITESPACE_RE.test(html))
        return html;
    const segments = segmentHtml(html);
    let result = '';
    let kept = 0;
    let before;
    for (const [position, segment] of segments.entries()) {
        if (isFormattingRun(html, before, segment, segments.at(position + 1)))
            continue;
        result += html.slice(segment.start, segment.end);
        kept += 1;
        // Tracking the kept segment is the same as tracking the previous one:
        // `flushText` emits at most one text run between any two tags, so a text
        // segment's predecessor is always a tag or a comment and is never itself
        // elided. Carrying it forward here rather than indexing backwards is what
        // keeps the decision local.
        before = segment;
    }
    return kept === segments.length ? html : result;
}
//# sourceMappingURL=render-template-whitespace.js.map